import React, { useState } from 'react';
import { errorText } from '../../pages/SoftwarePolicy/policyEdit';
import { familyEntries } from '../../pages/EndpointDevice/topSoftware';
import { scopeText, targetText } from './driftShared';

export const UPDATE_URL = 'cma/softwareinstall/update';
export const REMOVE_URL = 'cma/softwareinstall/remove';

const list = (v) => (Array.isArray(v) ? v : []);
const machineName = (item) => item.results?.computerName || item.deviceId;
const VERB = { update: 'Update', remove: 'Remove' };
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const most = (entries, field) => entries.reduce((m, p) => Math.max(m, num(p[field])), 0);

// The machines of one program on the CVE report, with the way to take care of it: tick machines, choose to update or remove the program, and put
// the jobs in their queues as a test (dry run) or for real after a confirmation. The page sends the program (its catalog id, or its name when
// it has none), the kind and the machine ids, never a script; the API works out each machine's own copy and the job.
// Only a program with a catalog id can be updated. onQueue(url, body) -> the API's answer {queued, refused, failed}.
export default function CveActions({ account, row, items, onQueue }) {
  const machines = items.filter((item) => familyEntries(item, row).length > 0);
  const canUpdate = Boolean(row.wingetId);
  const [action, setAction] = useState(canUpdate && row.unusedMachines < row.machines ? 'update' : 'remove');
  const [selected, setSelected] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const chosen = machines.filter((item) => selected[item.deviceId]);
  const allChosen = machines.length > 0 && chosen.length === machines.length;
  const verb = VERB[action];
  const tone = row.critical > 0 ? 'err' : row.high > 0 ? 'warn' : 'none';

  const queue = async (kind) => {
    setBusy(true);
    setOutcome(null);
    const program = row.wingetId ? { wingetId: row.wingetId } : { name: row.family };
    try {
      const result = await onQueue(action === 'update' ? UPDATE_URL : REMOVE_URL, { account, kind, deviceIds: chosen.map((m) => m.deviceId), ...program });
      setOutcome({ kind, ...result });
      setSelected({});
    } catch (ex) {
      setOutcome({ kind, error: errorText(ex) });
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  const queued = list(outcome?.queued);
  const problems = [
    ...list(outcome?.refused).map((r) => `Not queued, ${r.name}: ${r.error}`),
    ...list(outcome?.failed).map((r) => `Failed, ${r.name}: ${r.error}`),
  ];

  return (
    <div>
      <div className="v2-tools">
        <span className={`v2-pill ${tone}`}>{`Vulnerable ${machines.length}`}</span>
        <label className="v2-check">
          <input type="checkbox" aria-label={`Select all machines with ${row.family}`} checked={allChosen} disabled={busy}
            onChange={(e) => setSelected(e.target.checked ? Object.fromEntries(machines.map((m) => [m.deviceId, true])) : {})} />
          {`Select all (${machines.length})`}
        </label>
        <select className="v2-select" aria-label="What to do" value={action} disabled={busy} onChange={(e) => setAction(e.target.value)}>
          {canUpdate && <option value="update">Update</option>}
          <option value="remove">Remove</option>
        </select>
        <button type="button" className="v2-btn" disabled={chosen.length === 0 || busy} onClick={() => queue('check')}>{`Test (${chosen.length})`}</button>
        <button type="button" className="v2-btn primary" disabled={chosen.length === 0 || busy} onClick={() => setConfirming(true)}>{`Apply (${chosen.length})`}</button>
        {busy && <span className="v2-spin" role="status" aria-label="Queueing" />}
      </div>
      {outcome?.error && <div className="v2-alert err" role="alert">{outcome.error}</div>}
      {outcome && !outcome.error && (
        <div className={`v2-alert${problems.length ? ' info' : ''}`} role="status">
          {`${queued.length} ${outcome.kind === 'check' ? 'test' : 'apply'} ${queued.length === 1 ? 'job' : 'jobs'} in the queue.`}
          {problems.map((text) => <div className="v2-sub" key={text}>{text}</div>)}
        </div>
      )}
      <div className="v2-items">
        {machines.map((item) => {
          const entries = familyEntries(item, row);
          return (
            <div className="v2-app-machine" key={item.deviceId}>
              <div>
                <input type="checkbox" aria-label={`Select ${machineName(item)}`} disabled={busy}
                  checked={Boolean(selected[item.deviceId])} onChange={(e) => setSelected({ ...selected, [item.deviceId]: e.target.checked })} />
              </div>
              <div className="name">{machineName(item)}</div>
              <div>
                {entries.map((p) => (action === 'update' ? targetText(p.version, p.available) : p.version)).filter(Boolean).join(', ')}
                {[...new Set(entries.map((p) => scopeText(p.scope)).filter(Boolean))].map((text) => <div className="v2-sub" key={text}>{text}</div>)}
              </div>
              <div>{`Critical ${most(entries, 'critical')}, High ${most(entries, 'high')}`}<div className="v2-sub">{`Worst score ${most(entries, 'highestScore')}`}</div></div>
              <div>
                {entries.some((p) => p.unused) && <div>Not used lately</div>}
                {!canUpdate && <div className="v2-sub">No catalog id: it can be removed but not updated here.</div>}
              </div>
            </div>
          );
        })}
      </div>

      {confirming && (
        <div className="v2-dialog-host">
          <div className="v2-dialog" role="dialog" aria-label={`${verb} ${row.family}`}>
            <h2>{`${verb} ${row.family} on ${chosen.length} ${chosen.length === 1 ? 'machine' : 'machines'}?`}</h2>
            <p>{`A job will be put in the queue of each machine and run for real: it will ${action} ${row.family}.`}</p>
            {chosen.map((m) => <div key={m.deviceId}>{machineName(m)}</div>)}
            <div className="actions">
              <button type="button" className="v2-btn" onClick={() => setConfirming(false)} disabled={busy}>Cancel</button>
              <button type="button" className="v2-btn primary" onClick={() => queue('apply')} disabled={busy}>Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
